import React from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  ShieldCheckIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ServerIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  ClockIcon,
  BellAlertIcon,
  DocumentCheckIcon,
  WifiIcon,
  CircleStackIcon,
  ArrowPathIcon,
  ChartBarIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CpuChipIcon,
  FingerPrintIcon,
  GlobeAmericasIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const SecurityWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f3f4f6 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.03) 0%, transparent 70%);
    animation: ${gradientShift} 20s ease infinite;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  padding: var(--spacing-4xl) 0;
  position: relative;
  overflow: hidden;
  margin-top: -80px;
  padding-top: calc(var(--spacing-4xl) + 80px);
  
  @media (min-width: 768px) {
    margin-top: -90px;
    padding-top: calc(var(--spacing-4xl) + 90px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  position: relative;
  z-index: 1;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  margin-bottom: var(--spacing-xl);
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent);
    border-radius: 20px;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto var(--spacing-xl) auto;
  line-height: 1.8;
  animation: ${fadeIn} 1s ease-out;
`;

const LastUpdatedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.15);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 50px;
  color: white;
  font-size: 0.9rem;
  animation: ${fadeIn} 1.2s ease-out;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-3xl) var(--spacing-xl);
  position: relative;
  z-index: 1;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-3xl);
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  animation: ${fadeIn} 0.6s ease-out;
`;

const QuickNavTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const QuickNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
`;

const QuickNavItem = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  color: var(--text-secondary);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
    color: var(--davinci-blue);
    transform: translateX(4px);
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: var(--davinci-blue);
    flex-shrink: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: var(--spacing-2xl);
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: var(--spacing-2xl);
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%);
  border-radius: 12px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
  }
`;

const SectionContent = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  color: var(--davinci-midnight);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const SectionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 600;
`;

const SectionText = styled.p`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-lg);
`;

const SubSection = styled.div`
  margin-bottom: var(--spacing-xl);
  padding-left: var(--spacing-lg);
  border-left: 2px solid rgba(59, 130, 246, 0.2);
`;

const SubSectionTitle = styled.h3`
  color: var(--davinci-midnight);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 20px;
    height: 20px;
    color: var(--davinci-blue);
  }
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-xl);
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--davinci-blue) 0%, var(--davinci-beach) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:after {
    content: "✓";
    position: absolute;
    left: 5px;
    top: 8px;
    color: white;
    font-size: 12px;
    font-weight: bold;
  }
`;

const UnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-lg) 0;
`;

const MetricCard = styled.div`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
`;

const MetricIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 12px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-success);
  }
`;

const MetricContent = styled.div`
  flex: 1;
`;

const MetricTitle = styled.h4`
  color: var(--davinci-midnight);
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
`;

const MetricValue = styled.p`
  color: var(--text-secondary);
  margin: 0;
  
  strong {
    color: var(--davinci-midnight);
    font-weight: 600;
  }
`;

const SecurityHighlight = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(30, 58, 138, 0.05) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: var(--spacing-lg);
  margin: var(--spacing-lg) 0;
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-start;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--davinci-blue);
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const DataSecurity: React.FC = () => {
  const sections = [
    { id: 'purpose', title: 'Purpose', icon: <InformationCircleIcon /> },
    { id: 'scope', title: 'Scope', icon: <GlobeAmericasIcon /> },
    { id: 'security-measures', title: 'Security Measures', icon: <ShieldCheckIcon /> },
    { id: 'backup-recovery', title: 'Backup & Recovery', icon: <CloudArrowUpIcon /> },
    { id: 'incident-response', title: 'Incident Response', icon: <BellAlertIcon /> },
    { id: 'compliance', title: 'Compliance & Auditing', icon: <DocumentCheckIcon /> },
  ];

  return (
    <SecurityWrapper>
      <HeroSection>
        <HeroContent>
          <IconWrapper>
            <ShieldCheckIcon />
          </IconWrapper>
          <HeroTitle>Data Security Policy</HeroTitle>
          <HeroSubtitle>
            Enterprise-grade security measures protecting your data with encryption, access controls, 
            and comprehensive incident response protocols
          </HeroSubtitle>
          <LastUpdatedBadge>
            <InformationCircleIcon />
            Last Updated: August 29, 2025
          </LastUpdatedBadge>
        </HeroContent>
      </HeroSection>

      <Container>
        <ScrollAnimation>
          <QuickNav>
            <QuickNavTitle>
              <DocumentTextIcon />
              Security Framework Overview
            </QuickNavTitle>
            <QuickNavGrid>
              {sections.map((section) => (
                <QuickNavItem key={section.id} href={`#${section.id}`}>
                  {section.icon}
                  {section.title}
                </QuickNavItem>
              ))}
            </QuickNavGrid>
          </QuickNav>
        </ScrollAnimation>

        <ContentGrid>
          <ScrollAnimation>
            <SectionCard id="purpose">
              <SectionHeader>
                <SectionIcon>
                  <InformationCircleIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>1</SectionNumber>
                    Purpose
                  </SectionTitle>
                  <SectionText>
                    This policy outlines Davinci AI Solutions' commitment to protecting client data through 
                    robust security controls, including encryption, access management, and incident response protocols.
                  </SectionText>
                  <SecurityHighlight>
                    <ShieldCheckIcon />
                    <div>
                      <strong>Our Security Commitment:</strong> We implement industry-leading security measures to ensure 
                      your data remains protected at all times, meeting and exceeding regulatory requirements.
                    </div>
                  </SecurityHighlight>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="scope">
              <SectionHeader>
                <SectionIcon>
                  <GlobeAmericasIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>2</SectionNumber>
                    Scope
                  </SectionTitle>
                  <SectionText>
                    This policy applies to all data handled by Davinci AI Solutions, covering data at rest, 
                    in transit, and stored on cloud services, as well as the accounts we manage for clients.
                  </SectionText>
                  <UnorderedList>
                    <ListItem>Data at rest in our storage systems</ListItem>
                    <ListItem>Data in transit across networks</ListItem>
                    <ListItem>Cloud-based storage and services</ListItem>
                    <ListItem>Client account management systems</ListItem>
                  </UnorderedList>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="security-measures">
              <SectionHeader>
                <SectionIcon>
                  <ShieldCheckIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>3</SectionNumber>
                    Security Measures
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>
                      <LockClosedIcon />
                      3.1 Encryption
                    </SubSectionTitle>
                    <SectionText>
                      <strong>Data at Rest:</strong> All customer data stored within our systems is encrypted at rest 
                      with at least AES-128 encryption, ensuring strong protection of sensitive information.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>
                      <KeyIcon />
                      3.2 Access Control
                    </SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Password Management:</strong> Customer passwords are stored securely in industry-standard 
                        password management tools with multi-factor authentication (MFA), ensuring controlled and secure access.
                      </ListItem>
                      <ListItem>
                        <strong>Multi-Factor Authentication (MFA):</strong> Davinci AI Solutions is committed to using MFA 
                        on all company accounts and on all accounts within customer environments to the best of our ability, 
                        reinforcing security across access points.
                      </ListItem>
                    </UnorderedList>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>
                      <WifiIcon />
                      3.3 Network Security
                    </SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Firewall Protection:</strong> Firewalls are configured to monitor and control network 
                        traffic, safeguarding against unauthorized access and potential threats.
                      </ListItem>
                      <ListItem>
                        <strong>Virtual Private Network (VPN):</strong> Remote connections to client networks and our 
                        systems are secured with encrypted VPNs, restricting access to authorized personnel only.
                      </ListItem>
                    </UnorderedList>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="backup-recovery">
              <SectionHeader>
                <SectionIcon>
                  <CloudArrowUpIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>4</SectionNumber>
                    Data Backup and Recovery
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>
                      <CircleStackIcon />
                      4.1 Backup Schedule
                    </SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Frequency:</strong> All critical data is backed up daily to provide data resilience 
                        and ensure recovery options in case of data loss or system failure.
                      </ListItem>
                      <ListItem>
                        <strong>Storage Locations:</strong> Backups are securely stored on cloud services in Canada 
                        and the United States, with data encrypted to safeguard it against unauthorized access.
                      </ListItem>
                    </UnorderedList>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>
                      <ArrowPathIcon />
                      4.2 Disaster Recovery
                    </SubSectionTitle>
                    <MetricCard>
                      <MetricIcon>
                        <ClockIcon />
                      </MetricIcon>
                      <MetricContent>
                        <MetricTitle>Recovery Point Objective (RPO)</MetricTitle>
                        <MetricValue>
                          <strong>24 hours</strong> - We aim to maintain minimal data loss in case of a disaster
                        </MetricValue>
                      </MetricContent>
                    </MetricCard>
                    
                    <MetricCard>
                      <MetricIcon>
                        <ArrowPathIcon />
                      </MetricIcon>
                      <MetricContent>
                        <MetricTitle>Recovery Time Objective (RTO)</MetricTitle>
                        <MetricValue>
                          <strong>72 hours</strong> - We prioritize swift recovery to minimize client service interruptions
                        </MetricValue>
                      </MetricContent>
                    </MetricCard>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="incident-response">
              <SectionHeader>
                <SectionIcon>
                  <BellAlertIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>5</SectionNumber>
                    Incident Response and Breach Notification
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>
                      <ExclamationTriangleIcon />
                      5.1 Detection and Response
                    </SubSectionTitle>
                    <SectionText>
                      <strong>Monitoring:</strong> We utilize real-time monitoring to detect and respond promptly to 
                      potential security incidents. Suspicious activity or unauthorized access attempts are escalated 
                      to our security team.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>
                      <BellAlertIcon />
                      5.2 Client Notification
                    </SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Breach Notification:</strong> In the event of a breach affecting client data, we will 
                        notify impacted clients within 48 business hours, providing details on the incident and recommended 
                        actions to mitigate risks.
                      </ListItem>
                      <ListItem>
                        <strong>Post-Incident Action:</strong> Following a breach, we conduct a thorough analysis to 
                        identify the root cause and implement additional security measures to prevent future incidents.
                      </ListItem>
                    </UnorderedList>
                    
                    <MetricCard style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(185, 28, 28, 0.05) 100%)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                      <MetricIcon style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                        <ClockIcon style={{ color: 'var(--color-error)' }} />
                      </MetricIcon>
                      <MetricContent>
                        <MetricTitle>48-Hour Notification Guarantee</MetricTitle>
                        <MetricValue>
                          We commit to notifying affected clients within 48 business hours of discovering any data breach
                        </MetricValue>
                      </MetricContent>
                    </MetricCard>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>

          <ScrollAnimation>
            <SectionCard id="compliance">
              <SectionHeader>
                <SectionIcon>
                  <DocumentCheckIcon />
                </SectionIcon>
                <SectionContent>
                  <SectionTitle>
                    <SectionNumber>6</SectionNumber>
                    Compliance and Auditing
                  </SectionTitle>
                  
                  <SubSection>
                    <SubSectionTitle>
                      <ScaleIcon />
                      6.1 Regulatory Compliance
                    </SubSectionTitle>
                    <SectionText>
                      <strong>PIPEDA Compliance:</strong> We follow the Personal Information Protection and Electronic 
                      Documents Act (PIPEDA) to ensure the secure handling of personal data for Canadian clients.
                    </SectionText>
                  </SubSection>

                  <SubSection>
                    <SubSectionTitle>
                      <DocumentTextIcon />
                      6.2 Documentation and Record-Keeping
                    </SubSectionTitle>
                    <UnorderedList>
                      <ListItem>
                        <strong>Audit Logs:</strong> Detailed audit logs are maintained for critical systems, recording 
                        access and activity. These logs are retained for a minimum of one year.
                      </ListItem>
                      <ListItem>
                        <strong>Policy Review:</strong> Security policies are reviewed annually to ensure they remain 
                        effective and in line with regulatory requirements.
                      </ListItem>
                    </UnorderedList>
                    
                    <SecurityHighlight>
                      <CheckCircleIcon />
                      <div>
                        <strong>Continuous Improvement:</strong> Our security policies undergo annual reviews and updates 
                        to incorporate the latest security best practices and regulatory requirements.
                      </div>
                    </SecurityHighlight>
                  </SubSection>
                </SectionContent>
              </SectionHeader>
            </SectionCard>
          </ScrollAnimation>
        </ContentGrid>
      </Container>
    </SecurityWrapper>
  );
};

// Add missing icon component
const ScaleIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

export default DataSecurity;